import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../components/ui/Card';
import {
  Search, Star, MapPin, RefreshCw, AlertCircle, ChevronDown, ChevronRight,
  Download, MessageSquare, ExternalLink, History, Plus, ArrowUpDown, TriangleAlert,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// Scans belong to the selected brand — a brand only ever sees its own.
const brandId = () => (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);
const brandName = () => {
  try { return JSON.parse(localStorage.getItem('selectedBrand') || 'null')?.name || null; }
  catch { return null; }
};
const headers = () => {
  const id = brandId();
  return { 'Content-Type': 'application/json', ...(id ? { 'X-Brand-ID': id } : {}) };
};

/**
 * Rating buckets use the reserved status palette — they encode a state (healthy /
 * needs attention / critical), not a series. Every segment ships with a visible
 * label and count: on a white surface `warning` and the unrated grey sit under
 * 3:1, so colour never carries the meaning by itself.
 */
const BUCKETS = [
  { key: 'good',     label: '4.0 and above', color: '#0ca30c', test: (r) => r >= 4 },
  { key: 'mid',      label: '3.0 – 4.0',     color: '#fab219', test: (r) => r >= 3 && r < 4 },
  { key: 'critical', label: 'Below 3.0',     color: '#d03b3b', test: (r) => r < 3 },
  { key: 'unrated',  label: 'No reviews',    color: '#d1d5db', test: null },
];

const fmtNum = (v) => (v == null || isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-US'));
const fmtRating = (v) => (v == null || isNaN(Number(v)) ? '—' : Number(v).toFixed(1));

// Derive the city from an address like "Şişli/İstanbul"
function cityOf(loc) {
  const parts = String(loc || '').split(',').map(p => p.trim());
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i].includes('/')) return parts[i].split('/').pop().trim();
  }
  return parts.length >= 2 ? parts[parts.length - 2] : (parts[0] || '—');
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    + ' ' + dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function Stars({ value, size = 13 }) {
  const v = Number(value) || 0;
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={size} className={i <= Math.round(v) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
      ))}
    </span>
  );
}

/**
 * Weighted mean: each location's rating carries the weight of its review count.
 * A plain mean lets a 2-review 5.0 offset an 800-review 3.4 and misreports the
 * network. The plain mean is still shown, as a secondary line.
 */
function weightedRating(locations) {
  let num = 0, den = 0;
  for (const l of locations) {
    const r = Number(l.averageRating), n = Number(l.totalReviews);
    if (!isFinite(r) || !isFinite(n) || n <= 0) continue;
    num += r * n; den += n;
  }
  return den > 0 ? num / den : null;
}

export default function PlaceReviews() {
  const bid = brandId();
  const bName = brandName();
  const [keyword, setKeyword] = useState('');
  const [deep, setDeep] = useState(false);
  const [onlyTR, setOnlyTR] = useState(true);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [savedJobs, setSavedJobs] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [showNewScan, setShowNewScan] = useState(false);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState({ by: 'reviews', dir: 'desc' });
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPoll(), []);

  const poll = useCallback(async (jobId) => {
    try {
      const r = await fetch(`${API_BASE}/api/place-reviews/status/${jobId}`, {
        credentials: 'include', headers: headers(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setJob(j);
      // Keep polling while the all-reviews fetch runs after the scan itself.
      if ((j.status === 'done' || j.status === 'error') && j.allReviews?.status !== 'running') stopPoll();
      return j;
    } catch (e) {
      return null; // transient — keep polling
    }
  }, []);

  const loadSaved = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/place-reviews/jobs?limit=30`, {
        credentials: 'include', headers: headers(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const { jobs } = await r.json();
      return Array.isArray(jobs) ? jobs : [];
    } catch (e) {
      return [];
    } finally {
      setLoadingSaved(false);
    }
  }, []);

  // Open on the most recent saved scan. The scan itself hits a paid API and
  // takes minutes, so it only ever runs when the user asks for it.
  useEffect(() => {
    if (!bid) { setLoadingSaved(false); return; }
    let cancelled = false;
    // Switching brands must clear the previous brand's results, not leave them
    // on screen under the new brand's name.
    stopPoll(); setJob(null); setSavedJobs([]); setLoadingSaved(true); setExpanded(new Set());
    (async () => {
      const jobs = await loadSaved();
      if (cancelled) return;
      setSavedJobs(jobs);
      const latest = jobs.find(j => j.status === 'done') || jobs[0];
      if (latest) {
        const j = await poll(latest.jobId);
        if (!cancelled && (j?.status === 'running' || j?.allReviews?.status === 'running')) {
          pollRef.current = setInterval(() => poll(latest.jobId), 3000);
        }
      } else {
        setShowNewScan(true);
      }
    })();
    return () => { cancelled = true; };
  }, [bid, loadSaved, poll]);

  const openSaved = useCallback(async (jobId) => {
    stopPoll();
    setExpanded(new Set());
    setError(null);
    const j = await poll(jobId);
    if (j?.status === 'running' || j?.allReviews?.status === 'running') {
      pollRef.current = setInterval(() => poll(jobId), 3000);
    }
  }, [poll]);

  const start = useCallback(async () => {
    const kw = keyword.trim();
    if (kw.length < 2) { setError('Enter at least 2 characters.'); return; }
    setError(null); setStarting(true); setJob(null); setExpanded(new Set());
    stopPoll();
    try {
      const r = await fetch(`${API_BASE}/api/place-reviews/start`, {
        method: 'POST', credentials: 'include',
        headers: headers(),
        body: JSON.stringify({ keyword: kw, deep, onlyTR }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b.error || `HTTP ${r.status}`);
      }
      const { jobId } = await r.json();
      await poll(jobId);
      pollRef.current = setInterval(() => poll(jobId), 3000);
      loadSaved().then(setSavedJobs);
    } catch (e) {
      setError(e.message || 'Could not start the scan');
    } finally {
      setStarting(false);
    }
  }, [keyword, deep, onlyTR, poll, loadSaved]);

  // The scan keeps only the first page of reviews per location. Exporting every
  // review pages through each location on the paid API, so it runs once per
  // scan on request and the result is stored with the scan.
  const [reviewsBusy, setReviewsBusy] = useState(false);
  const allReviews = job?.allReviews || null;
  const fetchingReviews = allReviews?.status === 'running';

  const fetchAllReviews = useCallback(async (refresh = false) => {
    if (!job?.jobId) return;
    setReviewsBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/place-reviews/jobs/${job.jobId}/reviews${refresh ? '?refresh=1' : ''}`, {
        method: 'POST', credentials: 'include', headers: headers(),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b.error || `HTTP ${r.status}`);
      }
      await poll(job.jobId);
      stopPoll();
      pollRef.current = setInterval(() => poll(job.jobId), 3000);
    } catch (e) {
      setError(e.message || 'Could not start fetching reviews');
    } finally {
      setReviewsBusy(false);
    }
  }, [job?.jobId, poll]);

  const exportReviewsCsv = useCallback(async () => {
    if (!job?.jobId) return;
    setReviewsBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/place-reviews/jobs/${job.jobId}/reviews`, {
        credentials: 'include', headers: headers(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const { rows = [] } = await r.json();
      // Same filter as the table, so a filtered view exports only its locations.
      const q = filter.trim().toLowerCase();
      const out = q
        ? rows.filter(x => (x.location || '').toLowerCase().includes(q) || (x.address || '').toLowerCase().includes(q))
        : rows;
      const head = ['Location', 'Address', 'City', 'Location rating', 'Location reviews', 'Author', 'Rating',
        'Review', 'Date', 'Relative date', 'Language', 'Owner response', 'Owner response date', 'Review URL', 'placeId'];
      const body = out.map(x => [
        x.location, x.address, cityOf(x.address), fmtRating(x.locationRating), x.locationReviews ?? '',
        x.author, x.rating ?? '', x.text, x.date, x.relative, x.lang, x.ownerResponse, x.ownerResponseDate,
        x.url, x.placeId,
      ]);
      const csv = [head, ...body].map(row => row.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${(job?.keyword || 'locations').replace(/\s+/g, '_')}_reviews.csv`;
      a.click();
    } catch (e) {
      setError(e.message || 'Could not export reviews');
    } finally {
      setReviewsBusy(false);
    }
  }, [job?.jobId, job?.keyword, filter]);

  const toggle = (id) => setExpanded(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const locations = useMemo(() => job?.locations || [], [job]);
  const running = job?.status === 'running';
  const busy = starting || running;

  // ── Derived summary ─────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const rated = locations.filter(l => l.averageRating != null);
    const totalReviews = locations.reduce((s, l) => s + (Number(l.totalReviews) || 0), 0);
    const counts = { good: 0, mid: 0, critical: 0, unrated: locations.length - rated.length };
    for (const l of rated) {
      const r = Number(l.averageRating);
      if (r >= 4) counts.good++;
      else if (r >= 3) counts.mid++;
      else counts.critical++;
    }
    return {
      rated, totalReviews, counts,
      weighted: weightedRating(locations),
      plain: rated.length ? rated.reduce((s, l) => s + Number(l.averageRating), 0) / rated.length : null,
    };
  }, [locations]);

  // Locations dragging the network down: weak rating with enough reviews to be
  // a real signal rather than one bad day.
  const attention = useMemo(() => (
    locations
      .filter(l => l.averageRating != null && Number(l.averageRating) < 4 && Number(l.totalReviews) >= 20)
      .sort((a, b) => Number(a.averageRating) - Number(b.averageRating))
      .slice(0, 6)
  ), [locations]);

  const byCity = useMemo(() => {
    const m = new Map();
    for (const l of locations) {
      const c = cityOf(l.location);
      if (!m.has(c)) m.set(c, []);
      m.get(c).push(l);
    }
    return [...m.entries()]
      .map(([city, ls]) => ({
        city, count: ls.length,
        reviews: ls.reduce((s, l) => s + (Number(l.totalReviews) || 0), 0),
        rating: weightedRating(ls),
      }))
      .sort((a, b) => b.reviews - a.reviews);
  }, [locations]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = q
      ? locations.filter(l =>
          (l.name || '').toLowerCase().includes(q) ||
          (l.location || '').toLowerCase().includes(q))
      : [...locations];
    const dir = sort.dir === 'asc' ? 1 : -1;
    // Unrated rows have no position on a rating scale — park them last in both
    // directions instead of letting them lead an ascending sort.
    const val = {
      name: (l) => (l.name || '').toLowerCase(),
      city: (l) => cityOf(l.location).toLowerCase(),
      rating: (l) => (l.averageRating == null ? null : Number(l.averageRating)),
      reviews: (l) => Number(l.totalReviews) || 0,
    }[sort.by];
    return rows.sort((a, b) => {
      const x = val(a), y = val(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      if (typeof x === 'string') return x.localeCompare(y, 'tr') * dir;
      return (x - y) * dir;
    });
  }, [locations, filter, sort]);

  const setSortBy = (by) => setSort(s => ({
    by,
    dir: s.by === by ? (s.dir === 'desc' ? 'asc' : 'desc') : (by === 'name' || by === 'city' ? 'asc' : 'desc'),
  }));

  const exportCsv = () => {
    if (!visible.length) return;
    const head = ['Location', 'Address', 'City', 'Rating', 'Reviews', 'Google Maps', 'placeId'];
    const rows = visible.map(l => [
      l.name, l.location, cityOf(l.location), fmtRating(l.averageRating),
      l.totalReviews ?? '', l.googlePage, l.placeId,
    ]);
    const csv = [head, ...rows].map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(job?.keyword || 'locations').replace(/\s+/g, '_')}_locations.csv`;
    a.click();
  };

  // Alignment classes are written out in full — Tailwind only ships classes it
  // finds as literal strings, so an interpolated `text-${align}` is purged.
  const SortHead = ({ by, children, right = false }) => (
    <th className={`px-3 py-2.5 font-medium ${right ? 'text-right' : 'text-left'}`}>
      <button
        onClick={() => setSortBy(by)}
        className={`inline-flex items-center gap-1 hover:text-gray-700 ${sort.by === by ? 'text-gray-700' : ''}`}
      >
        {children}
        <ArrowUpDown size={11} className={sort.by === by ? 'text-gray-500' : 'text-gray-300'} />
      </button>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* ── Header + saved scans ───────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-50">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Location &amp; Review Explorer</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Google ratings and review volume across{' '}
                {bName ? <span className="font-medium text-gray-700">{bName}</span> : 'this brand'}
                {'’'}s location network.
              </p>
            </div>
          </div>
          {bid && (
            <button
              onClick={() => setShowNewScan(v => !v)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-all"
            >
              <Plus size={15} /> New scan
            </button>
          )}
        </div>

        {/* Saved scans — opening one is free; only "New scan" calls the paid API */}
        {!loadingSaved && savedJobs.length > 0 && (
          <div className="mt-5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-2">
              <History size={13} /> Saved scans
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {savedJobs.map(s => {
                const active = job?.jobId === s.jobId;
                return (
                  <button
                    key={s.jobId}
                    onClick={() => openSaved(s.jobId)}
                    className={`shrink-0 text-left px-3.5 py-2.5 rounded-xl border transition-all ${
                      active ? 'border-blue-300 bg-blue-50/60 ring-1 ring-blue-100' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800 capitalize">{s.keyword}</span>
                      {s.status !== 'done' && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          s.status === 'running' ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'
                        }`}>
                          {s.status}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">
                      {fmtDate(s.createdAt)} · {fmtNum(s.count)} locations
                      {s.weightedRating != null && <> · {fmtRating(s.weightedRating)} ★</>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Scan controls ───────────────────────────────────────────────── */}
        {showNewScan && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[260px]">
                <label htmlFor="pr-keyword" className="block text-xs font-medium text-gray-500 mb-1.5">
                  Brand or keyword
                </label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="pr-keyword"
                    type="text"
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !busy) start(); }}
                    placeholder="e.g. Europcar"
                    className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:ring-2 focus:ring-blue-100 outline-none transition-all hover:border-gray-300"
                  />
                </div>
              </div>

              <label className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs text-gray-600 cursor-pointer hover:border-gray-300 transition-all select-none">
                <input type="checkbox" checked={onlyTR} onChange={e => setOnlyTR(e.target.checked)} className="rounded border-gray-300" />
                Turkey only
              </label>

              <label
                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs text-gray-600 cursor-pointer hover:border-gray-300 transition-all select-none"
                title="Also scans districts and airports of major cities — slower"
              >
                <input type="checkbox" checked={deep} onChange={e => setDeep(e.target.checked)} className="rounded border-gray-300" />
                Deep scan
              </label>

              <button
                onClick={start}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all"
              >
                {busy ? <RefreshCw size={15} className="animate-spin" /> : <Search size={15} />}
                {running ? 'Scanning…' : 'Run scan'}
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-2.5">
              A scan queries a paid API province by province and takes several minutes. Saved scans above open instantly.
            </p>
          </div>
        )}

        {/* ── Progress ────────────────────────────────────────────────────── */}
        {running && (
          <div className="mt-5">
            <div className="flex justify-between text-xs text-gray-500 mb-1.5">
              <span>
                {job.phase === 'reviews' ? 'Fetching ratings & reviews…' : 'Searching locations…'}
                {' '}({fmtNum(job.count)} locations)
              </span>
              <span className="font-medium text-gray-600">{Math.round((job.progress || 0) * 100)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${Math.round((job.progress || 0) * 100)}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Errors ─────────────────────────────────────────────────────────── */}
      {(error || job?.status === 'error') && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-rose-800">{error ? 'Scan could not start' : 'The scan failed'}</p>
            <p className="text-xs text-rose-700 mt-1">{error || job?.error}</p>
          </div>
        </div>
      )}

      {locations.length > 0 && (
        <>
          {/* ── Headline numbers ──────────────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-5 rounded-2xl border border-gray-100 shadow-sm">
              <div className="p-2.5 rounded-xl w-fit bg-blue-50"><MapPin className="w-5 h-5 text-blue-600" /></div>
              <p className="text-sm font-medium text-gray-500 mt-4 mb-1">Locations</p>
              <p className="text-2xl font-bold text-gray-900">{fmtNum(locations.length)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {fmtNum(summary.rated.length)} rated · {fmtNum(summary.counts.unrated)} without reviews
              </p>
            </Card>

            <Card className="p-5 rounded-2xl border border-gray-100 shadow-sm">
              <div className="p-2.5 rounded-xl w-fit bg-violet-50"><MessageSquare className="w-5 h-5 text-violet-600" /></div>
              <p className="text-sm font-medium text-gray-500 mt-4 mb-1">Total Reviews</p>
              <p className="text-2xl font-bold text-gray-900">{fmtNum(summary.totalReviews)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {summary.rated.length ? `${fmtNum(Math.round(summary.totalReviews / summary.rated.length))} per rated location` : '—'}
              </p>
            </Card>

            <Card className="p-5 rounded-2xl border border-gray-100 shadow-sm">
              <div className="p-2.5 rounded-xl w-fit bg-amber-50"><Star className="w-5 h-5 text-amber-500" /></div>
              <p className="text-sm font-medium text-gray-500 mt-4 mb-1">Weighted Rating</p>
              <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                {fmtRating(summary.weighted)}
                {summary.weighted != null && <Stars value={summary.weighted} />}
              </p>
              <p className="text-xs text-gray-400 mt-1" title="Each location weighted by its review count">
                {fmtRating(summary.plain)} unweighted
              </p>
            </Card>

            <Card className="p-5 rounded-2xl border border-gray-100 shadow-sm">
              <div className="p-2.5 rounded-xl w-fit bg-rose-50"><TriangleAlert className="w-5 h-5 text-rose-600" /></div>
              <p className="text-sm font-medium text-gray-500 mt-4 mb-1">Below 4.0</p>
              <p className="text-2xl font-bold text-gray-900">{fmtNum(summary.counts.mid + summary.counts.critical)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {fmtNum(attention.length)} with 20+ reviews
              </p>
            </Card>
          </div>

          {/* ── Rating distribution ───────────────────────────────────────── */}
          <Card className="p-5 rounded-2xl border border-gray-100 shadow-sm">
            <p className="text-sm font-medium text-gray-700 mb-3">Rating distribution</p>
            <div className="flex gap-[2px] h-7 rounded-lg overflow-hidden">
              {BUCKETS.map(b => {
                const n = summary.counts[b.key];
                if (!n) return null;
                const pct = (n / locations.length) * 100;
                return (
                  <div
                    key={b.key}
                    className="flex items-center justify-center min-w-[26px] first:rounded-l-lg last:rounded-r-lg"
                    style={{ width: `${pct}%`, backgroundColor: b.color }}
                    title={`${b.label}: ${n} locations`}
                  >
                    {pct >= 7 && (
                      <span className={`text-xs font-semibold ${b.key === 'unrated' ? 'text-gray-600' : 'text-white'}`}>
                        {n}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Legend — identity never rests on colour alone */}
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3">
              {BUCKETS.map(b => (
                <span key={b.key} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: b.color }} />
                  {b.label}
                  <span className="font-medium text-gray-800">{summary.counts[b.key]}</span>
                </span>
              ))}
            </div>
          </Card>

          {/* ── Needs attention ───────────────────────────────────────────── */}
          {attention.length > 0 && (
            <Card className="p-5 rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <TriangleAlert size={15} className="text-rose-600" />
                <p className="text-sm font-medium text-gray-700">Needs attention</p>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Below 4.0 with at least 20 reviews — enough volume that the rating reflects a pattern, not one bad week.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {attention.map(l => (
                  <div key={l.placeId || l.name} className="border border-gray-100 rounded-xl p-3.5 hover:border-gray-200 transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate" title={l.name}>{l.name}</div>
                        <div className="text-xs text-gray-400 truncate">{cityOf(l.location)}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-lg font-bold" style={{ color: Number(l.averageRating) < 3 ? '#d03b3b' : '#b45309' }}>
                          {fmtRating(l.averageRating)}
                        </div>
                        <div className="text-[11px] text-gray-400">{fmtNum(l.totalReviews)} reviews</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* ── City breakdown ────────────────────────────────────────────── */}
          {byCity.length > 1 && (
            <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-700">By city</p>
              </div>
              <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-medium">City</th>
                      <th className="px-4 py-2.5 text-right font-medium">Locations</th>
                      <th className="px-4 py-2.5 text-right font-medium">Reviews</th>
                      <th className="px-4 py-2.5 text-right font-medium">Weighted Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {byCity.map(c => (
                      <tr key={c.city} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-gray-800">{c.city}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{fmtNum(c.count)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{fmtNum(c.reviews)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="font-medium text-gray-800">{fmtRating(c.rating)}</span>
                            {c.rating != null && <Star size={12} className="text-amber-400 fill-amber-400" />}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* ── All locations ─────────────────────────────────────────────── */}
          <Card className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
            <div className="flex flex-wrap justify-between items-center gap-3 px-4 py-3 border-b border-gray-100">
              <span className="font-medium text-gray-800 text-sm">
                {fmtNum(visible.length)}
                {visible.length !== locations.length && <span className="text-gray-400"> / {fmtNum(locations.length)}</span>}
                {' '}locations
              </span>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    placeholder="Filter by name or address"
                    className="pl-8 pr-3 py-1.5 w-56 bg-white border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>
                <button
                  onClick={exportCsv}
                  title="One row per location"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:border-gray-300 hover:text-gray-900 transition-all"
                >
                  <Download size={14} /> Locations CSV
                </button>
                {job?.status === 'done' && (allReviews?.status === 'done' ? (
                  <button
                    onClick={exportReviewsCsv}
                    disabled={reviewsBusy}
                    title="One row per review"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition-all"
                  >
                    {reviewsBusy ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
                    Reviews CSV ({fmtNum(allReviews.count)})
                  </button>
                ) : (
                  <button
                    onClick={() => fetchAllReviews(false)}
                    disabled={reviewsBusy || fetchingReviews}
                    title={`Pages through every location on the paid API — about ${fmtNum(Math.ceil(summary.totalReviews / 10))} requests`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition-all"
                  >
                    {(reviewsBusy || fetchingReviews) ? <RefreshCw size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                    {fetchingReviews ? 'Fetching reviews…' : allReviews?.status === 'stalled' || allReviews?.status === 'error' ? 'Resume fetching reviews' : 'Fetch all reviews'}
                  </button>
                ))}
              </div>
            </div>
            {fetchingReviews && (
              <div className="px-4 py-3 border-b border-gray-100 bg-blue-50/40">
                <div className="flex justify-between text-xs text-gray-600 mb-1.5">
                  <span>
                    Fetching every review · {fmtNum(allReviews.doneLocations)} / {fmtNum(allReviews.totalLocations)} locations
                    · {fmtNum(allReviews.count)} reviews so far
                  </span>
                  <span className="font-medium">
                    {allReviews.totalLocations ? Math.round(allReviews.doneLocations / allReviews.totalLocations * 100) : 0}%
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{ width: `${allReviews.totalLocations ? Math.round(allReviews.doneLocations / allReviews.totalLocations * 100) : 0}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">Runs on the server — you can leave this page. Reviews CSV unlocks when it finishes.</p>
              </div>
            )}
            {allReviews?.status === 'error' && (
              <div className="px-4 py-2.5 border-b border-gray-100 text-xs text-rose-700 bg-rose-50">
                Fetching reviews stopped: {allReviews.error}. Resume continues from the last finished location.
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs">
                  <tr>
                    <th className="px-3 py-2.5 w-6"></th>
                    <SortHead by="name">Location</SortHead>
                    <SortHead by="city">City</SortHead>
                    <SortHead by="rating" right>Rating</SortHead>
                    <SortHead by="reviews" right>Reviews</SortHead>
                    <th className="px-3 py-2.5 text-center font-medium">Maps</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visible.map((l) => {
                    const id = l.placeId || l.searchId || l.name;
                    const isOpen = expanded.has(id);
                    const hasSamples = (l.sampleReviews || []).length > 0;
                    return (
                      <React.Fragment key={id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2.5">
                            {hasSamples && (
                              <button
                                onClick={() => toggle(id)}
                                className="p-1 rounded text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                                title={isOpen ? 'Hide sample reviews' : 'Show sample reviews'}
                              >
                                {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-gray-800">{l.name}</div>
                            <div className="text-xs text-gray-400">{l.location}</div>
                          </td>
                          <td className="px-3 py-2.5 text-gray-600">{cityOf(l.location)}</td>
                          <td className="px-3 py-2.5 text-right">
                            <span className="inline-flex items-center gap-1.5 justify-end">
                              <span className="font-medium text-gray-800">{fmtRating(l.averageRating)}</span>
                              {l.averageRating != null && <Star size={13} className="text-amber-400 fill-amber-400" />}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-700">{fmtNum(l.totalReviews)}</td>
                          <td className="px-3 py-2.5 text-center">
                            {l.googlePage && (
                              <a
                                href={l.googlePage}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                              >
                                Open <ExternalLink size={11} />
                              </a>
                            )}
                          </td>
                        </tr>
                        {isOpen && hasSamples && (
                          <tr className="bg-gray-50/60">
                            <td></td>
                            <td colSpan={5} className="px-3 py-3">
                              <div className="space-y-2">
                                {l.sampleReviews.map((rv, i) => (
                                  <div key={i} className="text-xs bg-white border border-gray-100 rounded-lg p-2.5">
                                    <div className="flex items-center gap-2 mb-1">
                                      <MessageSquare size={12} className="text-gray-400" />
                                      <span className="font-medium text-gray-700">{rv.author || 'Anonymous'}</span>
                                      {rv.rating != null && <Stars value={rv.rating} />}
                                      <span className="text-gray-400">{rv.relative}</span>
                                    </div>
                                    <div className="text-gray-600">{rv.text || '—'}</div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-10 text-center text-sm text-gray-500">
                        No location matches “{filter}”.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* ── Empty states ───────────────────────────────────────────────────── */}
      {job?.status === 'done' && locations.length === 0 && (
        <Card className="rounded-xl border border-gray-100 py-14 text-center">
          <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">No locations found</p>
          <p className="text-xs text-gray-400 mt-1">Try a broader keyword or turn off “Turkey only”.</p>
        </Card>
      )}

      {!bid && (
        <Card className="rounded-xl border border-gray-100 py-14 text-center">
          <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">No brand selected</p>
          <p className="text-xs text-gray-400 mt-1">Pick a brand first — scans are stored per brand.</p>
        </Card>
      )}

      {bid && !job && !busy && !loadingSaved && (
        <Card className="rounded-xl border border-gray-100 py-14 text-center">
          <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">
            No scans yet for {bName || 'this brand'}
          </p>
          <p className="text-xs text-gray-400 mt-1">Run a scan to map this brand's locations and their Google ratings.</p>
        </Card>
      )}
    </div>
  );
}
